import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Card, Form, Row, Col, Spinner, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAddresses, Address } from "../hooks/use-addresses";
import {
  useCountries,
  useProvinces,
  useCities,
  Country,
  Province,
  City,
} from "../../../hooks/use-locations";
import { ShippingInfo } from "../hooks/use-save-order";
import "./AddressSelector.css";
import { NeighborhoodSelect, NO_NEIGHBORHOOD } from "../../common/NeighborhoodSelect";

interface AddressSelectorProps {
  onAddressSelect: (
    addressId: string | null,
    shippingInfo: ShippingInfo,
  ) => void;
  selectedAddressId?: string | null;
  shippingInfo?: ShippingInfo | null;
  onSelectDeliveryType: (deliveryType: "DELIVERY" | "STORE_PICKUP") => void;
  deliveryType: "DELIVERY" | "STORE_PICKUP";
  onConfirmAndContinue?: () => void;
  setDestination: React.Dispatch<
    React.SetStateAction<{ city: string; province: string; address: string; neighborhood_code?: string }>
  >;
}

export interface AddressSelectorRef {
  showConfirmation: () => void;
}

const AddressSelector = forwardRef<AddressSelectorRef, AddressSelectorProps>(
  (
    {
      onAddressSelect,
      selectedAddressId: propSelectedAddressId = "",
      shippingInfo: propShippingInfo,
      onSelectDeliveryType,
      deliveryType,
      onConfirmAndContinue,
      setDestination,
    },
    ref,
  ) => {
    const navigate = useNavigate();

    // Obtenemos las direcciones del usuario usando nuestro hook personalizado
    const { addresses, isLoading, error } = useAddresses();

    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
      propSelectedAddressId,
    );
    const [showNewAddressForm, setShowNewAddressForm] = useState<boolean>(
      propSelectedAddressId === "new",
    );

    // Estado para el formulario de nueva dirección
    const [newAddress, setNewAddress] = useState<ShippingInfo>(
      propShippingInfo || {
        name: "",
        phone: "",
        country_id: "",
        province_id: "",
        city_id: "",
        address: "",
        postal_code: "",
        neighborhood: "No aplica",
        neighborhood_code: "",
        observations: "",

        // QUIÉN RECIBE
        receiver: {
          name: "",
          phone: "",
          type: "self",
          document: "",
        },
      },
    );

    // Estados para IDs de ubicaciones - inicializar desde propShippingInfo
    const [selectedCountryId, setSelectedCountryId] = useState<string | null>(
      propShippingInfo?.country_id || null,
    );
    const [selectedProvinceId, setSelectedProvinceId] = useState<string | null>(
      propShippingInfo?.province_id || null,
    );
    const [isStorePickup, setIsStorePickup] = useState(
      deliveryType === "STORE_PICKUP",
    );
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    // Usar los hooks de ubicaciones
    const { countries } = useCountries();
    const { provinces } = useProvinces(selectedCountryId);
    const { cities } = useCities(selectedProvinceId);

    // Manejar cambios en el formulario de nueva dirección
    const handleNewAddressChange = React.useCallback(
      (field: string, value: string) => {
        setNewAddress((prev) => {
          const updated = {
            ...prev,
            [field]: value,
          };

          onAddressSelect("new", updated);
          /* if (field === 'city_id' && value) {
        const selectedCity = cities.find((city: City) => city.id === value);
        setShippingCost(selectedCity?.shippingCost ?? 0);
      } */
          return updated;
        });
      },
      [onAddressSelect],
    );

    // Efecto para manejar la selección automática de direcciones
    const isStorePickupRef = React.useRef(isStorePickup);

    useEffect(() => {
      isStorePickupRef.current = isStorePickup;
    }, [isStorePickup]);

    useEffect(() => {
      if (countries?.length && showNewAddressForm) {
        const colombia = countries.find(
          (c: any) => c.name?.toLowerCase() === "colombia",
        );

        if (colombia) {
          handleNewAddressChange("country_id", colombia.id);
          setSelectedCountryId(colombia.id);
        }
      }
    }, [countries, showNewAddressForm, handleNewAddressChange]);

    const handleAddressChange = (addressId: string) => {
      setSelectedAddressId(addressId);
      setShowNewAddressForm(addressId === "new");

      if (addressId !== "new") {
        const selectedAddress = addresses.find(
          (addr: Address) => addr.id === addressId,
        );
        if (selectedAddress) {
          // Convertir la dirección seleccionada al formato esperado por el componente padre
          const shippingInfo: ShippingInfo = {
            name: "",
            phone: "",
            country_id: "",
            province_id: "",
            city_id: "",
            address: "",
            postal_code: "",
            neighborhood: "No aplica",
            neighborhood_code: "",
            observations: "",
            receiver: {
              name: "",
              phone: "",
              type: "self",
              document: "",
            },
          };

          onAddressSelect(addressId, shippingInfo);
        }
      } else {
        onAddressSelect("new", {
          name: "",
          phone: "",
          country_id: "",
          province_id: "",
          city_id: "",
          address: "",
          postal_code: "",
          neighborhood: "No aplica",
          neighborhood_code: "",
          observations: "",
          receiver: {
            name: "",
            phone: "",
            type: "self",
            document: "",
          },
        });
      }
    };

    /* const addShippingCost = (address: Address) => {
    if (!address?.id) return;

    const shipppingCost = address.city_id.shippingCost ?? address.country_id.shippingCost ?? 0;
    setShippingCost(shipppingCost);
  }
 */
    const handleReceiverChange = React.useCallback(
      (field: keyof ShippingInfo["receiver"], value: string) => {
        setNewAddress((prev) => {
          const updated = {
            ...prev,
            receiver: {
              ...prev.receiver,
              [field]: value,
            },
          };

          onAddressSelect("new", updated);

          return updated;
        });
      },
      [onAddressSelect],
    );

    const resetAddressSelection = () => {
      onAddressSelect(null, {
        name: "",
        phone: "",
        country_id: "",
        province_id: "",
        city_id: "",
        address: "",
        postal_code: "",
        neighborhood: "No aplica",
        neighborhood_code: "",
        observations: "",
        receiver: {
          name: "",
          phone: "",
          type: "self",
          document: "",
        },
      });
    };
    // Renderizar las direcciones guardadas
    const renderSavedAddresses = () => {
      if (isLoading) {
        return (
          <div className="text-center py-3">
            <Spinner animation="border" />
          </div>
        );
      }

      if (error) {
        return (
          <div className="alert alert-danger">
            Error al cargar las direcciones
          </div>
        );
      }

      if (addresses.length === 0) {
        return (
          <div className="alert alert-info">
            No tienes direcciones guardadas. Por favor, agrega una nueva
            dirección.
          </div>
        );
      }

      return (
        <Row className="g-3">
          {addresses.map((address: Address) => (
            <Col md={12} key={address.id}>
              <Card
                className={`address-card cursor-pointer h-100 ${selectedAddressId === address.id ? "selected" : ""}`}
                onClick={() => {
                  handleAddressChange(address.id);
                  setIsStorePickup(false);
                  onSelectDeliveryType("DELIVERY");
                  setDestination({
                    city: address.city_id.name,
                    province: address.province_id?.name || "",
                    address: address.address,
                    neighborhood_code: address.neighborhood_code || "",
                  });
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-start">
                    <div className="address-radio me-3">
                      <Form.Check
                        type="radio"
                        id={`savedAddress-${address.id}`}
                        name="savedAddress"
                        checked={selectedAddressId === address.id}
                        onChange={() => {}}
                        className="m-0"
                      />
                    </div>
                    <div className="address-content flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        <i className="ri-map-pin-line me-2 text-primary"></i>
                        <strong className="address-name">{address.name}</strong>
                      </div>
                      <p className="text-muted small mb-1">
                        {address.address}
                        {address.neighborhood && address.neighborhood !== "No aplica" && ` · ${address.neighborhood}`}
                      </p>
                      <p className="text-muted small mb-1">
                        {address.city_id.name}, {address.province_id.name},{" "}
                        {address.postal_code}
                      </p>
                    </div>
                    {selectedAddressId === address.id && (
                      <div className="address-check">
                        <i className="ri-checkbox-circle-fill text-success fs-4"></i>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      );
    };

    // Función para obtener la dirección seleccionada formateada
    const getSelectedAddressDisplay = () => {
      if (isStorePickup) {
        return {
          title: "Recoger en Oficina Multydrink de Medellín",
          details: "Recogerás tu pedido directamente en nuestra oficina de Medellín. Sin costo de envío.",
        };
      }
      if (selectedAddressId === "new") {
        // Obtener nombres de país, departamento y ciudad
        const countryName =
          countries.find((c: Country) => c.id === newAddress.country_id)
            ?.name || "";
        const provinceName =
          provinces.find((p: Province) => p.id === newAddress.province_id)
            ?.name || "";
        const cityName =
          cities.find((c: City) => c.id === newAddress.city_id)?.name || "";

        const locationParts = [
          newAddress.address,
          newAddress.neighborhood !== "No aplica" && newAddress.neighborhood,
          cityName,
          provinceName,
          countryName,
          newAddress.postal_code,
        ].filter(Boolean);

        return {
          title: newAddress.name || "Nueva dirección",
          details: locationParts.join(", ") || "Dirección incompleta",
        };
      }
      const address = addresses.find(
        (addr: Address) => addr.id === selectedAddressId,
      );
      if (address) {
        return {
          title: address.name,
          details: `${address.address}${address.neighborhood && address.neighborhood !== "No aplica" ? `, ${address.neighborhood}` : ""}, ${address.city_id.name}, ${address.province_id.name}, ${address.postal_code}`,
        };
      }
      return null;
    };

    // Manejar confirmación de dirección
    const handleConfirmAddress = () => {
      setShowConfirmModal(false);
      if (onConfirmAndContinue) {
        if (selectedAddressId === "new") {
          setDestination({
            city:
              cities.find((c: City) => c.id === newAddress.city_id)?.name || "",
            province:
              provinces.find((p: Province) => p.id === newAddress.province_id)
                ?.name || "",
            address: newAddress.address,
            neighborhood_code: newAddress.neighborhood_code || "",
          });
        }

        onConfirmAndContinue();
      }
    };

    // Validar formulario de nueva dirección
    const validateNewAddressForm = (): boolean => {
      const errors: Record<string, string> = {};

      if (!newAddress.name?.trim()) {
        errors.name = "El alias de la dirección es requerido";
      } else if (newAddress.name.length > 15) {
        errors.name = "El alias admite máximo 15 caracteres";
      }

      if (!newAddress.country_id) {
        errors.country_id = "Selecciona un país";
      }

      if (!newAddress.province_id) {
        errors.province_id = "Selecciona un departamento/estado";
      }

      if (!newAddress.city_id) {
        errors.city_id = "Selecciona una ciudad";
      }

      if (!newAddress.address?.trim()) {
        errors.address = "La dirección es requerida";
      } else if (newAddress.address.length > 50) {
        errors.address = `La dirección tiene ${newAddress.address.length} caracteres y el máximo permitido es 50.`;
      }

      if (newAddress.postal_code && !/^[0-9]{1,10}$/.test(newAddress.postal_code)) {
        errors.postal_code = "El código postal debe ser numérico, máximo 10 dígitos";
      }

      if (
        newAddress.receiver?.type === "other" &&
        !newAddress.receiver?.name?.trim()
      ) {
        errors.receiver_name = "El nombre de quien recibe es requerido";
      }

      if (
        newAddress.receiver?.type === "other" &&
        !newAddress.receiver?.phone?.trim()
      ) {
        errors.receiver_phone = "El teléfono es requerido";
      }

      if (
        newAddress.receiver?.type === "other" &&
        !newAddress.receiver?.document?.trim()
      ) {
        errors.receiver_document = "El documento es requerido";
      } else if (
        newAddress.receiver?.type === "other" &&
        !/^[0-9]+$/.test(newAddress.receiver.document!)
      ) {
        errors.receiver_document = "El documento solo admite números";
      }

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };

    // Exponer función para mostrar modal de confirmación al componente padre
    useImperativeHandle(ref, () => ({
      showConfirmation: () => {
        // Si es nueva dirección, validar primero
        if (selectedAddressId === "new") {
          const isValid = validateNewAddressForm();
          if (!isValid) {
            return; // No mostrar modal si hay errores
          }
        }
        setShowConfirmModal(true);
      },
    }));

    return (
      <div className="address-selector">
        <h3 className="mb-4">Dirección de envío</h3>

        <Form>
          {/* Opción de recoger en tienda */}
          <Card
            className={`address-card cursor-pointer mb-4 ${isStorePickup ? "selected" : ""}`}
            onClick={() => {
              setIsStorePickup(true);
              onSelectDeliveryType("STORE_PICKUP");
              setShowNewAddressForm(false);
              setSelectedAddressId(null);
              resetAddressSelection();
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center">
                <div className="address-radio me-3">
                  <Form.Check
                    type="radio"
                    checked={isStorePickup}
                    onChange={() => {}}
                    className="m-0"
                  />
                </div>
                <div className="d-flex align-items-center flex-grow-1">
                  <i className="ri-store-2-line me-2 text-primary fs-4"></i>
                  <div>
                    <strong>Recoger en Oficina Multydrink de Medellín</strong>
                    <p className="text-muted small mb-0">Sin costo de envío</p>
                  </div>
                </div>
                {isStorePickup && (
                  <i className="ri-checkbox-circle-fill text-success fs-4"></i>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Direcciones guardadas */}
          {addresses.length > 0 && (
            <div className="mb-4">{renderSavedAddresses()}</div>
          )}

          {/* Opción de nueva dirección */}
          <Card
            className={`address-card cursor-pointer mb-4 ${selectedAddressId === "new" ? "selected" : ""}`}
            onClick={() => {
              handleAddressChange("new");
              setIsStorePickup(false);
              onSelectDeliveryType("DELIVERY");
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center">
                <div className="address-radio me-3">
                  <Form.Check
                    type="radio"
                    id="newAddress"
                    name="savedAddress"
                    checked={selectedAddressId === "new"}
                    onChange={() => {}}
                    className="m-0"
                  />
                </div>
                <div className="d-flex align-items-center flex-grow-1">
                  <i className="ri-add-circle-line me-2 text-primary fs-4"></i>
                  <div>
                    <strong>Usar una nueva dirección</strong>
                    <p className="text-muted small mb-0">
                      Agregar una dirección de envío diferente
                    </p>
                  </div>
                </div>
                {selectedAddressId === "new" && (
                  <i className="ri-checkbox-circle-fill text-success fs-4"></i>
                )}
              </div>
            </Card.Body>
          </Card>

          {showNewAddressForm && (
            <Row>
              <Col md={12}>
                <Card className="bg-white border-0 rounded-3">
                  <Card.Body>
                    <h5 className="mb-3 fw-semibold">Nueva dirección</h5>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        ¿Quién recibe el pedido?
                      </Form.Label>

                      <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-4 mt-2">
                        <Form.Check
                          inline
                          type="radio"
                          name="receiver_type"
                          id="receiver-self"
                          label="Yo recibiré el pedido"
                          checked={
                            (newAddress.receiver?.type || "self") === "self"
                          }
                          onChange={() => handleReceiverChange("type", "self")}
                        />

                        <Form.Check
                          inline
                          type="radio"
                          name="receiver_type"
                          id="receiver-other"
                          label="Otra persona recibirá el pedido"
                          checked={newAddress.receiver?.type === "other"}
                          onChange={() => handleReceiverChange("type", "other")}
                        />
                      </div>
                    </Form.Group>

                    {newAddress.receiver?.type === "other" && (
                      <Row>
                        <Col xs={12} sm={6} lg={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Nombre completo</Form.Label>
                            <Form.Control
                              placeholder="Ej. María Pérez"
                              value={newAddress.receiver?.name || ""}
                              onChange={(e) =>
                                handleReceiverChange("name", e.target.value)
                              }
                              isInvalid={!!formErrors.receiver_name}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={12} sm={6} lg={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número de documento</Form.Label>
                            <Form.Control
                              placeholder="Ej. 1020304050"
                              inputMode="numeric"
                              value={newAddress.receiver?.document || ""}
                              onChange={(e) =>
                                handleReceiverChange(
                                  "document",
                                  e.target.value.replace(/\D/g, ""),
                                )
                              }
                              isInvalid={!!formErrors.receiver_document}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={12} sm={6} lg={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Teléfono de contacto</Form.Label>
                            <Form.Control
                              placeholder="Ej. 300 123 4567"
                              value={newAddress.receiver?.phone || ""}
                              onChange={(e) =>
                                handleReceiverChange("phone", e.target.value)
                              }
                              isInvalid={!!formErrors.receiver_phone}
                            />
                          </Form.Group>
                        </Col>

                      </Row>
                    )}

                    <Row>
                      <Col xs={12} sm={6} lg={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Alias dirección</Form.Label>
                          <Form.Control
                            value={newAddress.name}
                            maxLength={15}
                            onChange={(e) =>
                              handleNewAddressChange("name", e.target.value)
                            }
                            placeholder="Ej. Casa, Trabajo"
                            isInvalid={!!formErrors.name}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Dirección</Form.Label>
                          <Form.Control
                            value={newAddress.address}
                            maxLength={50}
                            onChange={(e) =>
                              handleNewAddressChange("address", e.target.value)
                            }
                            placeholder="Ej. Cra. 34 # 11B - 70"
                            isInvalid={!!formErrors.address}
                          />
                          <Form.Text muted>
                            {(newAddress.address || "").length} / 50 caracteres
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col xs={12} sm={6} lg={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Código postal</Form.Label>
                          <Form.Control
                            placeholder="Ej. 050021"
                            maxLength={10}
                            value={newAddress.postal_code}
                            onChange={(e) =>
                              handleNewAddressChange(
                                "postal_code",
                                e.target.value,
                              )
                            }
                            isInvalid={!!formErrors.postal_code}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={12} sm={6} lg={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>País</Form.Label>
                          <Form.Select
                            style={{ height: "50px" }}
                            value={newAddress.country_id}
                            onChange={(e) => {
                              setSelectedCountryId(e.target.value);
                              setSelectedProvinceId(null);
                              handleNewAddressChange(
                                "country_id",
                                e.target.value,
                              );
                              handleNewAddressChange("province_id", "");
                              handleNewAddressChange("city_id", "");
                              handleNewAddressChange("neighborhood", NO_NEIGHBORHOOD);
                              handleNewAddressChange("neighborhood_code", "");
                            }}
                            isInvalid={!!formErrors.country_id}
                          >
                            <option value="">Selecciona</option>
                            {countries.map((c: Country) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12} sm={6} lg={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Departamento / Provincia</Form.Label>
                          <Form.Select
                            style={{ height: "50px" }}
                            value={newAddress.province_id}
                            onChange={(e) => {
                              setSelectedProvinceId(e.target.value);
                              handleNewAddressChange(
                                "province_id",
                                e.target.value,
                              );
                              handleNewAddressChange("city_id", "");
                              handleNewAddressChange("neighborhood", NO_NEIGHBORHOOD);
                              handleNewAddressChange("neighborhood_code", "");
                            }}
                            isInvalid={!!formErrors.province_id}
                          >
                            <option value="">Selecciona</option>
                            {provinces.map((p: Province) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12} sm={6} lg={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Ciudad</Form.Label>
                          <Form.Select
                            style={{ height: "50px" }}
                            value={newAddress.city_id}
                            onChange={(e) =>
                              handleNewAddressChange("city_id", e.target.value)
                            }
                            isInvalid={!!formErrors.city_id}
                          >
                            <option value="">Selecciona</option>
                            {cities.map((c: City) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={12} sm={6} lg={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Barrio / Corregimiento (opcional)</Form.Label>
                          <NeighborhoodSelect
                            provinceId={newAddress.province_id || null}
                            cityId={newAddress.city_id || null}
                            code={newAddress.neighborhood_code}
                            onChange={({ name, code }) => {
                              handleNewAddressChange("neighborhood", name);
                              handleNewAddressChange("neighborhood_code", code);
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} lg={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Observaciones</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={newAddress.observations || ""}
                            onChange={(e) =>
                              handleNewAddressChange(
                                "observations",
                                e.target.value,
                              )
                            }
                            placeholder="Ej. Frente al supermercado, portón negro"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* PREVIEW */}
                    <div className="mb-3 p-3 border rounded">
                      <small className="text-muted d-block mb-1">
                        Vista previa de dirección:
                      </small>

                      <strong>
                        {newAddress.address + (newAddress.observations ? ` ${newAddress.observations}` : "") || "Dirección incompleta"}
                      </strong>

                      <div className="small text-muted">
                        {[
                          countries.find(
                            (c: any) => c.id === newAddress.country_id,
                          )?.name,
                          provinces.find(
                            (p: any) => p.id === newAddress.province_id,
                          )?.name,
                          cities.find((c: any) => c.id === newAddress.city_id)
                            ?.name,
                          newAddress.postal_code,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>

                      {formErrors.address && (
                        <div className="text-danger small mt-1">
                          {formErrors.address}
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Form>

        {/* Modal de confirmación de dirección */}
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>
              <i className="ri-map-pin-line me-2 text-primary"></i>
              Confirmar dirección
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2">
            {getSelectedAddressDisplay() && (
              <Card className="bg-light border-0">
                <Card.Body>
                  <h6 className="mb-2">
                    <i className="ri-checkbox-circle-fill text-success me-2"></i>
                    {getSelectedAddressDisplay()?.title}
                  </h6>
                  <p className="text-muted mb-0 small">
                    {getSelectedAddressDisplay()?.details}
                  </p>
                </Card.Body>
              </Card>
            )}
            <p className="mt-3 mb-0 text-center text-muted">
              ¿Deseas continuar con esta dirección de envío?
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
            <div>
              {selectedAddressId &&
                selectedAddressId !== "new" &&
                !isStorePickup && (
                  <Button
                    variant="outline-warning"
                    onClick={() => {
                      setShowConfirmModal(false);
                      navigate("/settings/address");
                    }}
                  >
                    <i className="ri-edit-line me-1"></i>
                    Editar dirección
                  </Button>
                )}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cambiar dirección
              </Button>
              <Button variant="primary" onClick={handleConfirmAddress}>
                <i className="ri-check-line me-1"></i>
                Confirmar y continuar
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    );
  },
);

export default AddressSelector;